/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  IconButton,
  Link,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/joy';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add,
  Delete as DeleteIcon,
  Edit,
  Launch as LaunchIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
  Subtitles as SubtitlesIcon,
  Visibility,
} from '@mui/icons-material';
import DeleteAlert from '../../../main/widgets/DeleteAlert';
import PermissionGate from '../../../main/components/PermissionGate';
import { dataGridNativeCheckboxSlots } from '../../../main/components/dataGridNativeCheckboxSlots';
import { OmrAmount } from '../../../main/components/OmrAmount';
import OmrSymbol from '../../../main/components/OmrSymbol';
import { getLocationDisplayName, toLocationWritePk } from '../../../main/utils/locationDisplayName';
import { labelForEventTypeCode } from '../../../main/utils/trackingEventLabels';
import useTrackingEventTypeOptions from '../../../main/hooks/useTrackingEventTypeOptions';
import { translateAxiosErrorMessage } from '../../../main/utils/masterApiError';
import {
  shipmentStatusChipSx,
  shipmentStatusJoyColor,
  shipmentStatusLabel,
} from '../../../main/utils/shipmentStatusDisplay';
import {
  deleteShipmentPiece,
  extractShipmentPieceFieldErrors,
  getShipmentPiece,
  patchShipmentPiece,
} from '../../../main/API/shipmentPiece';
import { PERMISSIONS } from '../../../main/context/AuthContext';
import { usePermissions } from '../../../main/hooks/usePermissions';
import CreateShipmentPieceModal from './CreateShipmentPieceModal';
import ShipmentPieceEditModal from './ShipmentPieceEditModal';
import ShipmentPiecesStickersWindow from './ShipmentPiecesStickersWindow';
import { shipmentPiecesDataGridSx } from './shipmentPiecesDataGridSx';
import { useTrackingEventPrompt } from '../../../main/hooks/useTrackingEventPrompt';
import {
  applyTrackingEventControl,
  buildPieceTrackingSnapshot,
} from '../../../main/utils/trackingEventControl';

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return '—';
  }
}

function buildEditFormFromPiece(piece) {
  if (!piece) return {};
  return {
    sequence: piece.sequence == null ? '' : String(piece.sequence),
    description: piece.description || '',
    weight_kg: piece.weight_kg == null ? '' : String(piece.weight_kg),
    width_cm: piece.width_cm == null ? '' : String(piece.width_cm),
    length_cm: piece.length_cm == null ? '' : String(piece.length_cm),
    height_cm: piece.height_cm == null ? '' : String(piece.height_cm),
    volumetric_weight_kg:
      piece.volumetric_weight_kg == null ? '' : String(piece.volumetric_weight_kg),
    status: piece.status || '',
    event_type: piece.event_type || '',
    note: piece.note || '',
    product_category_code:
      piece.product_category_code == null ? '' : String(piece.product_category_code),
    meter_price_override:
      piece.meter_price_override == null ? '' : String(piece.meter_price_override),
    extra_cost: piece.extra_cost == null ? '' : String(piece.extra_cost),
  };
}

/**
 * Shared shipment-pieces DataGrid with toolbar actions (add, print stickers,
 * delete) and row actions (view, edit, tracking, print sticker, delete).
 *
 * Used by ShipmentPiecesPanel (embedded on shipment form) and can be wired
 * into ShipmentPiecesManagerScreen for a single source of truth.
 */
export default function ShipmentPiecesTable({
  rows = [],
  loading = false,
  lockedShipmentId = null,
  onRefresh,
  hideShipmentColumn = false,
  viewMode = 'active',
  paginationMode = 'client',
  rowCount,
  paginationModel: controlledPaginationModel,
  onPaginationModelChange,
  onRecoverOne,
}) {
  const { t, i18n } = useTranslation('ui');
  const { hasPermission } = usePermissions();

  const canView = hasPermission(PERMISSIONS.SHIPMENT_PIECES_VIEW);
  const canCreate = hasPermission(PERMISSIONS.SHIPMENT_PIECES_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.SHIPMENT_PIECES_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.SHIPMENT_PIECES_DELETE);
  const canRecover = hasPermission(PERMISSIONS.SHIPMENT_PIECES_RECOVER);
  const canPrintLabel = hasPermission(PERMISSIONS.SHIPMENT_PIECES_LABEL);
  const canPrintBatch = hasPermission(PERMISSIONS.SHIPMENT_PIECES_LABEL_BATCH);
  const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);

  const { options: eventTypeOptions } = useTrackingEventTypeOptions(canListEventTypes);
  const { promptIfNeeded, trackingPromptModal } = useTrackingEventPrompt();
  const editOriginalTrackingSnapshotRef = useRef(null);

  const [selectionModel, setSelectionModel] = useState([]);
  const [internalPaginationModel, setInternalPaginationModel] = useState({ page: 0, pageSize: 25 });

  const paginationModel = controlledPaginationModel ?? internalPaginationModel;
  const setPaginationModel = onPaginationModelChange ?? setInternalPaginationModel;

  const [createOpen, setCreateOpen] = useState(false);
  const [editPiece, setEditPiece] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editEventTypeOpt, setEditEventTypeOpt] = useState(null);
  const [editLocation, setEditLocation] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editFieldErrors, setEditFieldErrors] = useState({});

  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteAlertSingle, setDeleteAlertSingle] = useState(false);
  const [selectedPieceIds, setSelectedPieceIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [piecesStickersWindowOpened, setPiecesStickersWindowOpened] = useState(false);
  const [stickerPieces, setStickerPieces] = useState([]);

  const [successDelete, setSuccessDelete] = useState(false);
  const [failedDelete, setFailedDelete] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const canPrintSelection =
    selectionModel.length === 1
      ? canPrintLabel
      : selectionModel.length > 1
        ? canPrintBatch
        : false;

  const selectedPieces = useMemo(
    () => (rows ?? []).filter((piece) => selectionModel.some((id) => id === piece.id)),
    [rows, selectionModel]
  );

  const openEdit = useCallback(
    async (row) => {
      if (!canView) return;
      try {
        const res = await getShipmentPiece(row.id);
        const piece = res?.data || row;
        setEditPiece(piece);
        setEditForm(buildEditFormFromPiece(piece));
        setEditLocation(piece.location && typeof piece.location === 'object' ? piece.location : null);
        setEditEventTypeOpt(
          piece.event_type ? { code: piece.event_type, name: piece.event_type } : null
        );
        editOriginalTrackingSnapshotRef.current = buildPieceTrackingSnapshot(
          buildEditFormFromPiece(piece),
          piece.location && typeof piece.location === 'object' ? piece.location : null,
        );
        setEditFieldErrors({});
      } catch (e) {
        setResponseMessage(translateAxiosErrorMessage(e, t) || t('shipmentPieces.errorLoad'));
        setFailedNotification(true);
      }
    },
    [canView, t]
  );

  const closeEdit = () => {
    setEditPiece(null);
    setEditForm({});
    setEditEventTypeOpt(null);
    setEditLocation(null);
    setEditFieldErrors({});
  };

  const saveEdit = async () => {
    if (!editPiece || !canUpdate) return;
    setSavingEdit(true);
    setEditFieldErrors({});
    try {
      const trackingDecision = await promptIfNeeded({
        isCreate: false,
        original: editOriginalTrackingSnapshotRef.current,
        current: buildPieceTrackingSnapshot(editForm, editLocation),
        entityType: 'shipment_piece',
      });
      if (trackingDecision === null) {
        return;
      }
      const payload = {};
      const numKeys = [
        'sequence',
        'weight_kg',
        'width_cm',
        'length_cm',
        'height_cm',
        'volumetric_weight_kg',
        'meter_price_override',
        'extra_cost',
      ];
      Object.entries(editForm).forEach(([k, v]) => {
        if (v === '' || v == null) {
          payload[k] = numKeys.includes(k) ? null : k === 'status' || k === 'event_type' ? '' : null;
        } else if (numKeys.includes(k)) {
          payload[k] = k === 'sequence' ? Number(v) : v;
        } else {
          payload[k] = v;
        }
      });
      payload.location = toLocationWritePk(editLocation);
      const finalPayload = applyTrackingEventControl(payload, trackingDecision);
      await patchShipmentPiece(editPiece.id, finalPayload);
      setResponseMessage(t('shipmentPieces.savedSuccess'));
      setSuccessNotification(true);
      closeEdit();
      setSelectionModel((prev) => prev.filter((id) => id !== editPiece.id));
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      const errs = extractShipmentPieceFieldErrors(e);
      if (Object.keys(errs).length) setEditFieldErrors(errs);
      setResponseMessage(translateAxiosErrorMessage(e, t) || t('shipmentPieces.errorLoad'));
      setFailedNotification(true);
    } finally {
      setSavingEdit(false);
    }
  };

  const deletePiecesFromTable = async (ids) => {
    if (!canDelete || !ids?.length) {
      setDeleteAlert(false);
      setDeleteAlertSingle(false);
      return;
    }
    setDeleteLoading(true);
    try {
      await Promise.all(ids.map((id) => deleteShipmentPiece(id)));
      setSuccessDelete(true);
      setSelectionModel([]);
      if (typeof onRefresh === 'function') await onRefresh();
    } catch (e) {
      setFailedDelete(true);
      setDeleteErrorMsg(
        translateAxiosErrorMessage(e, t) || t('shipmentPieces.deleteErrorGeneric')
      );
    } finally {
      setDeleteLoading(false);
      setDeleteAlert(false);
      setDeleteAlertSingle(false);
    }
  };

  const openStickersForPieces = (pieces) => {
    if (!pieces?.length) return;
    setStickerPieces(pieces);
    setPiecesStickersWindowOpened(true);
  };

  const baseColumns = useMemo(() => {
    const cols = [
      { field: 'id', headerName: t('shipmentPieces.idCol'), flex: 1, minWidth: 70 },
      {
        field: 'human_label',
        headerName: t('shipmentPieces.humanLabel'),
        flex: 1.5,
        minWidth: 110,
        renderCell: (params) => params.row.human_label || '—',
      },
      {
        field: 'barcode',
        headerName: t('shipmentPieces.barcode'),
        flex: 2,
        minWidth: 160,
        renderCell: (params) => (
          <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
            {params.row.barcode || '—'}
          </Typography>
        ),
      },
    ];

    if (!hideShipmentColumn) {
      cols.push({
        field: 'shipment',
        headerName: t('shipmentPieces.shipmentCol'),
        flex: 1.2,
        minWidth: 100,
        renderCell: (params) => {
          const sid = params.row.shipment;
          if (!sid) return '—';
          return (
            <Link
              href={`/Shipment/${sid}/EditShipment`}
              target="_blank"
              rel="noopener"
              onClick={(e) => e.stopPropagation()}
            >
              #{sid}
            </Link>
          );
        },
      });
    }

    cols.push(
      {
        field: 'sequence',
        headerName: t('shipmentPieces.sequence'),
        flex: 0.8,
        minWidth: 70,
        type: 'number',
      },
      {
        field: 'status',
        headerName: t('shipmentPieces.status'),
        flex: 1.5,
        minWidth: 130,
        renderCell: (params) =>
          params.row.status ? (
            <Chip
              size="sm"
              color={shipmentStatusJoyColor(params.row.status)}
              variant="soft"
              sx={shipmentStatusChipSx(params.row.status)}
            >
              {shipmentStatusLabel(params.row.status, t)}
            </Chip>
          ) : (
            '—'
          ),
      },
      {
        field: 'location',
        headerName: t('shipmentPieces.location'),
        flex: 2,
        minWidth: 140,
        renderCell: (params) =>
          params.row.location
            ? getLocationDisplayName(params.row.location, i18n.language)
              || params.row.location.name
              || '—'
            : '—',
      },
      {
        field: 'chargeable_weight_kg',
        headerName: t('shipmentPieces.chargeableWeight'),
        flex: 1.2,
        minWidth: 110,
        renderCell: (params) =>
          params.row.chargeable_weight_kg != null && params.row.chargeable_weight_kg !== ''
            ? `${params.row.chargeable_weight_kg} kg`
            : '—',
      },
      {
        field: 'price',
        headerName: t('shipmentPieces.piecePrice'),
        flex: 1.2,
        minWidth: 100,
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{t('shipmentPieces.piecePrice')}</span>
            <OmrSymbol height={14} />
          </Box>
        ),
        renderCell: (params) => <OmrAmount value={params.row.price} empty="—" />,
      },
      {
        field: 'event_type',
        headerName: t('shipmentPieces.eventType'),
        flex: 1.8,
        minWidth: 150,
        renderCell: (params) =>
          params.row.event_type
            ? labelForEventTypeCode(params.row.event_type, eventTypeOptions)
            : '—',
      },
      {
        field: 'modified_at',
        headerName: t('shipmentPieces.lastEvent'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => formatDateTime(params.row.modified_at),
      }
    );

    return cols;
  }, [t, i18n.language, eventTypeOptions, hideShipmentColumn]);

  const activeActionsColumn = useMemo(
    () => ({
      field: 'actions',
      headerName: t('shipments.colActions'),
      flex: 3,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {canView && (
            <Tooltip title={t('shipmentPieces.actions.view')}>
              <IconButton
                size="sm"
                variant="soft"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(params.row);
                }}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title={t('shipmentPieces.tooltipEditPiece')}>
              <IconButton
                size="sm"
                variant="soft"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  openEdit(params.row);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {params.row.barcode && (
            <Tooltip title={t('shipmentPieces.actions.viewTracking')}>
              <IconButton
                size="sm"
                variant="soft"
                color="neutral"
                component="a"
                href={`/track/${encodeURIComponent(params.row.barcode)}`}
                target="_blank"
                rel="noopener"
                onClick={(e) => e.stopPropagation()}
              >
                <LaunchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canPrintLabel && (
            <Tooltip title={t('shipments.tooltipPrintStickers')}>
              <IconButton
                size="sm"
                variant="soft"
                color="info"
                onClick={(e) => {
                  e.stopPropagation();
                  openStickersForPieces([params.row]);
                }}
              >
                <SubtitlesIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title={t('shipmentPieces.tooltipDeletePiece')}>
              <IconButton
                size="sm"
                variant="soft"
                color="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPieceIds([params.row.id]);
                  setDeleteAlertSingle(true);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    }),
    [t, canView, canUpdate, canPrintLabel, canDelete, openEdit]
  );

  const deletedActionsColumn = useMemo(
    () => ({
      field: 'actions',
      headerName: t('shipments.colActions'),
      flex: 1.5,
      minWidth: 80,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canRecover && (
            <Tooltip title={t('shipmentPieces.tooltipRecoverPiece')}>
              <IconButton
                size="sm"
                variant="soft"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof onRecoverOne === 'function') onRecoverOne(params.row.id);
                }}
              >
                <RestoreFromTrashIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    }),
    [t, canRecover, onRecoverOne]
  );

  const columns = useMemo(
    () => [...baseColumns, viewMode === 'deleted' ? deletedActionsColumn : activeActionsColumn],
    [baseColumns, viewMode, deletedActionsColumn, activeActionsColumn]
  );

  const handlePaginationModelChange = (params) => {
    if (params.pageSize !== paginationModel.pageSize) {
      setPaginationModel({ page: 0, pageSize: params.pageSize });
    } else {
      setPaginationModel({ ...paginationModel, page: params.page });
    }
  };

  return (
    <>
      {trackingPromptModal}
      {viewMode === 'active' && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {(canPrintLabel || canPrintBatch) && (
            <Tooltip
              title={
                selectionModel.length === 0
                  ? t('shipmentPieces.tooltipPrintSelect')
                  : t('shipments.tooltipPrintStickers')
              }
            >
              <span>
                <IconButton
                  onClick={() => openStickersForPieces(selectedPieces)}
                  disabled={selectionModel.length === 0 || !canPrintSelection}
                  color="info"
                  size="lg"
                >
                  <SubtitlesIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <PermissionGate permission={PERMISSIONS.SHIPMENT_PIECES_DELETE}>
            <Tooltip title={t('shipments.tooltipDelete')}>
              <IconButton
                onClick={() => setDeleteAlert(true)}
                disabled={selectionModel.length === 0}
                color="danger"
                size="lg"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.SHIPMENT_PIECES_CREATE}>
            <Tooltip title={t('shipmentPieces.tooltipAdd')}>
              <IconButton onClick={() => setCreateOpen(true)} color="primary" size="lg">
                <Add />
              </IconButton>
            </Tooltip>
          </PermissionGate>
        </Box>
      )}

      <DataGrid
        slots={dataGridNativeCheckboxSlots}
        checkboxSelection={
          viewMode === 'active' || (viewMode === 'deleted' && canRecover)
        }
        onRowSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
        rowSelectionModel={selectionModel}
        onPaginationModelChange={handlePaginationModelChange}
        pagination
        paginationModel={paginationModel}
        paginationMode={paginationMode}
        rowCount={paginationMode === 'server' ? (rowCount ?? 0) : undefined}
        rows={rows || []}
        columns={columns}
        loading={loading}
        pageSizeOptions={[25, 50, 100]}
        hideFooterSelectedRowCount
        getRowHeight={() => 'auto'}
        getRowClassName={() => (viewMode === 'deleted' ? 'deleted-piece-row' : '')}
        sx={{
          ...shipmentPiecesDataGridSx,
          ...(viewMode === 'deleted'
            ? {
                '& .deleted-piece-row': {
                  backgroundColor: 'rgba(0,0,0,0.02)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                },
              }
            : {}),
        }}
      />

      <ShipmentPiecesStickersWindow
        pieces={stickerPieces}
        lockedShipmentId={lockedShipmentId}
        open={piecesStickersWindowOpened}
        onClose={() => {
          setPiecesStickersWindowOpened(false);
          setStickerPieces([]);
        }}
      />

      <DeleteAlert
        label={t('shipmentPieces.deleteAlertPieces')}
        open={deleteAlert}
        onDelete={() => deletePiecesFromTable(selectionModel)}
        onClose={() => setDeleteAlert(false)}
        loading={deleteLoading}
      />
      <DeleteAlert
        label={t('shipmentPieces.deleteAlertPiece')}
        open={deleteAlertSingle}
        onDelete={() => deletePiecesFromTable(selectedPieceIds)}
        onClose={() => setDeleteAlertSingle(false)}
        loading={deleteLoading}
      />

      <CreateShipmentPieceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        lockedShipmentId={lockedShipmentId}
        onCreated={async () => {
          setCreateOpen(false);
          setResponseMessage(t('shipmentPieces.createdSuccess'));
          setSuccessNotification(true);
          if (typeof onRefresh === 'function') await onRefresh();
        }}
      />

      <ShipmentPieceEditModal
        open={Boolean(editPiece)}
        piece={editPiece}
        form={editForm}
        onFormChange={setEditForm}
        eventTypeOpt={editEventTypeOpt}
        onEventTypeChange={setEditEventTypeOpt}
        location={editLocation}
        onLocationChange={setEditLocation}
        fieldErrors={editFieldErrors}
        canUpdate={canUpdate}
        canListEventTypes={canListEventTypes}
        saving={savingEdit}
        onClose={closeEdit}
        onSave={saveEdit}
      />

      <Snackbar
        autoHideDuration={3000}
        open={successDelete}
        variant="soft"
        color="success"
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSuccessDelete(false);
        }}
      >
        {t('shipmentPieces.deletedSuccess')}
      </Snackbar>
      <Snackbar
        autoHideDuration={3000}
        open={failedDelete}
        variant="soft"
        color="danger"
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setFailedDelete(false);
        }}
      >
        {deleteErrorMsg}
      </Snackbar>
      <Snackbar
        autoHideDuration={3000}
        open={successNotification}
        variant="soft"
        color="success"
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setSuccessNotification(false);
        }}
      >
        {responseMessage}
      </Snackbar>
      <Snackbar
        autoHideDuration={3000}
        open={failedNotification}
        variant="soft"
        color="danger"
        onClose={(_, reason) => {
          if (reason === 'clickaway') return;
          setFailedNotification(false);
        }}
      >
        {responseMessage}
      </Snackbar>
    </>
  );
}
